import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Flex,
  Icon,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { Button as RsButton } from 'rsuite';
import { FaPlus } from 'react-icons/fa';
import { useHotglue } from '@hotglue/widget';
import store from '../../../redux/store';
import apiEndpoint from '../../config/data';
import { Button as NexusButton, showToast } from '@spotoninc/nexus-react';
import { fetchWithAuth } from '../../../utils/apiHelpers';
import { AddIcon } from '@spotoninc/nexus-web-assets';
import { useFeatureFlagFromContext } from '../../../launchdarkly';

// Import custom components
import IntegrationList from './IntegrationList';
import AddIntegrationModal from './AddIntegrationModal';
import BudgetSettingsModal from './BudgetSettingsModal';
import BudgetAlertDialog from './BudgetAlertDialog';

// Import services and utilities
import {
  objToJson,
  updateIntegrationConnectionStatus,
  checkConnection,
} from './integrationUtils';
import { getBlid } from '../../../utils/cookieHelpers';
import {
  trackIntegrationAdded,
  trackIntegrationConnected,
  trackIntegrationDisconnected,
  addEventProperties,
  addUserProperties,
} from '../../../utils/analytics';

/**
 * Main Integration Settings Component
 */
export default function IntegrationSettingHook(props) {
  // State for modals
  const [connectModal, setConnectModal] = useState(false);
  const [enableBudgetingModal, setEnableBudgetingModal] = useState(false);

  // State for integration data
  const [apps, setApps] = useState([
    {
      id: '',
      app_name: 'Quickbook',
      company_id: '123456789',
      integration_type: 'Online',
      last_sync: '2023-12-23',
      daily_sync: 'On',
      connected: true,
    },
  ]);
  const [integrationsLoaded, setIntegrationsLoaded] = useState(false);

  // State for form data
  const [appName, setAppName] = useState('');
  const [captureLocation, setCaptureLocation] = useState(false);
  const [quickbooksType, setQuickbooksType] = useState(false);
  const [locationAttr, setLocationAttr] = useState(false);
  const [integration_type, setIntegration_type] = useState();
  const [uploadBtnLoading, setUploadBtnLoading] = useState(false);
  const [excelUploadData, setExcelUploadData] = useState({
    from_date: '',
    to_date: '',
    value: '',
  });
  const [limitUploader, setLimitUploader] = useState([]);
  const [modalButtonLoading, setModalButtonLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [int_id, setInt_id] = useState('');
  const [companyId, setCompanyId] = useState();
  const [newIntegration, setNewIntegration] = useState();
  const [hotglueFlowId, setHotglueFlowId] = useState();
  const [sourceId, setSourceId] = useState();
  const [integrationTypes, setIntegrationTypes] = useState([]);
  const [allIntegrationTypes, setAllIntegrationTypes] = useState([]);
  const [tpc, setTpc] = useState();
  const [captureLocationDisplay, setCaptureLocationDisplay] = useState(false);
  const [locationAttrDisplay, setLocationAttrDisplay] = useState(false);
  const [tenant, setTenant] = useState();
  const [hotglueNewLink, setHotglueNewLink] = useState();
  const [attributeType, setAttributeType] = useState(false);
  const [periodTypes, setPeriodTypes] = useState([]);
  const [periodCalType, setPeriodCalType] = useState('');
  const [periodCalTypeInt, setPeriodCalTypeInt] = useState('');
  const [periodCalTypeFlag, setPeriodCalTypeFlag] = useState(false);
  const [periodCalTypeFlagInt, setPeriodCalTypeFlagInt] = useState(false);
  const [companySwitcherActive, setCompanySwitcherActive] = useState(undefined);

  // State for loading indicators
  const [syncButtonLoading, setSyncButtonLoading] = useState(false);
  const [saveBtnLoading, setSaveBtnLoading] = useState(false);
  const [editButtonLoading, setEditButtonLoading] = useState(false);

  // State for budget settings
  const [enableBudgeting, setEnableBudgeting] = useState(false);
  const [locationClassLevelBudgeting, setLocationClassLevelBudgeting] =
    useState(false);
  const [yearBasedBudgeting, setYearBasedBudgeting] = useState(false);
  const [propotionalBudgeting, setPropotionalBudgeting] = useState(false);
  const [enableBudgetingM, setEnableBudgetingM] = useState(false);
  const [locationClassLevelBudgetingM, setLocationClassLevelBudgetingM] =
    useState(false);
  const [yearBasedBudgetingM, setYearBasedBudgetingM] = useState(false);
  const [propotionalBudgetingM, setPropotionalBudgetingM] = useState(false);
  const [locationAttrM, setLocationAttrM] = useState(false);

  // State for other data
  const [metadata, setMetadata] = useState();
  const [country, setCountry] = useState();
  const [countrySelected, setCountrySelected] = useState();
  // Default period type for new integrations - hardcoded to '13 periods' as the standard default
  // This value is only used when creating new integrations, not when editing existing ones
  const [defaultPeriod, setDefaultPeriod] = useState('13 periods');
  const [cashAc, setCashAc] = useState('');
  const [cashAcInt, setCashAcInt] = useState('');
  const [editedAppName, setEditedAppName] = useState(''); // Integration name for editing
  const [sendEditDataFlag, setSendEditDataFlag] = useState(false);
  // Map of callbacks keyed by integration ID - prevents race conditions when multiple integrations are edited
  const onEditCompleteCallbacksRef = useRef(new Map());

  // Feature flags
  const showReskin = useFeatureFlagFromContext('showReskin', false);
  const oktaSsoAuthEnabled = useFeatureFlagFromContext('oktaSsoAuth', false);

  // Chakra UI hooks
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef();

  // Hotglue hooks
  const { link, createJob } = useHotglue();

  // Cash/Accrual data
  const cashAccData = [
    { value: 'Cash', label: 'Cash' },
    { value: 'Accrual', label: 'Accrual' },
  ];

  /**
   * Update a specific integration in the apps array without causing a full re-render
   * @param {String} integrationId - ID of the integration to update
   * @param {Object} updatedFields - Fields to update in the integration
   */
  const updateIntegrationLocally = useCallback(
    (integrationId, updatedFields) => {
      if (!apps) return;

      // Create a new array with the updated integration
      const updatedApps = apps.map(app => {
        if (app.id === integrationId) {
          return { ...app, ...updatedFields };
        }
        return app;
      });

      // Update the state with the new array
      setApps(updatedApps);
    },
    [apps]
  );

  /**
   * Fetch integrations from API
   */
  const fetchIntegrations = useCallback(async () => {
    // Only set apps to undefined when we're doing a full refresh
    // This is what causes the flickering
    setApps(undefined);
    try {
      const response = await fetchWithAuth('/api/auth_update/', {
        method: 'GET',
      });
      const data = await response.json();

      setApps(data.integration);
      setAllIntegrationTypes(data.integration_types);

      const itype = data.integration_types.map(item => ({
        value: item.integration_type,
        label: item.integration_desc,
      }));

      const ptype = data.period_types.map(item => ({
        value: item,
        label: item,
      }));

      setMetadata(data.metadata);
      setIntegrationTypes(itype);
      setPeriodTypes(ptype);
      setDefaultPeriod('13 periods');
      setIntegrationsLoaded(true); // Mark as successfully loaded

      // Update Heap event properties with integration connection status
      updateIntegrationConnectionStatus(data.integration, addEventProperties);
    } catch (error) {
      console.error('Error fetching integrations:', error);
      // Silently handle the error - show empty state instead of alert
      setApps([]);
      // Don't set integrationsLoaded to true on error - this prevents
      // isFirstIntegration from incorrectly returning true
    }
  }, []);

  /**
   * Fetch country data from API
   */
  const fetchCountry = useCallback(async () => {
    try {
      await fetchWithAuth('/api/country/', {
        method: 'GET',
      })
        .then(response => response.json())
        .then(data => {
          data.forEach(
            obj =>
              (obj.country_currency =
                obj.country_label + ' (' + obj.currency + ') ')
          );

          data.forEach(obj => {
            obj.label = obj.country_currency;
            delete obj.country_currency;
          });

          data.forEach(obj => {
            obj.value = obj.country_id;
            delete obj.country_id;
          });

          setCountry(data);
        })
        .catch(() => console.error('Error fetching countries'));
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  }, []);

  /**
   * Helper function to check if this is the first integration being added
   * @returns {boolean} - True if no integrations exist yet (regardless of connection status)
   */
  const isFirstIntegrationAdded = () => {
    // Only return true if integrations have been successfully loaded
    // This prevents incorrectly sending BLID when fetch fails or during initial load
    if (!integrationsLoaded) {
      return false; // Fail-safe: don't send BLID if we can't determine
    }

    // Check if there are any integrations at all (connected or not)
    return !apps || apps.length === 0;
  };

  /**
   * Helper function to check if this is the first integration being connected
   * @returns {boolean} - True if no integrations are currently connected
   */
  const isFirstIntegrationConnected = () => {
    // Only return true if integrations have been successfully loaded
    if (!integrationsLoaded) {
      return false; // Fail-safe
    }

    if (!apps || apps.length === 0) {
      return true;
    }

    // Check if any integration is already connected using checkConnection utility
    // which checks refresh_token_expired status
    const connectedIntegrations = apps.filter(app => checkConnection(app));
    return connectedIntegrations.length === 0;
  };

  /**
   * Connect authentication for an integration
   * @param {String} id - Integration ID
   */
  const connectAuth = useCallback(
    async id => {
      const data = new FormData();
      data.append('integration_id', id);
      localStorage.setItem('integration_id', id);

      // Check if this is the first integration being connected (for analytics and BLID)
      const isFirstConnected = isFirstIntegrationConnected();

      // Only send BLID if oktaSsoAuth is enabled
      // This feature is SSO-specific and avoids unnecessary checks for non-SSO users
      if (oktaSsoAuthEnabled) {
        // If this is the first integration being connected, add blid to the request
        // Backend will automatically update status to FullyIntegrated
        if (isFirstConnected) {
          const blid = getBlid();
          if (blid) {
            data.append('blid', blid);
          } else {
            console.warn(
              'First integration connected but no BLID found in cookies'
            );
          }
        }
      }

      try {
        await fetchWithAuth('/api/connect_auth/', {
          method: 'POST',
          body: data,
        })
          .then(response => {
            if (!response.ok) {
              throw new Error(
                `Server responded with status: ${response.status}`
              );
            }
            return response.json();
          })
          .then(responseData => {
            // Check if the response contains an error message
            if (responseData && responseData.error) {
              throw new Error(responseData.error || 'Unknown error occurred');
            }

            // Check if the response contains a status message
            const statusMessage =
              responseData && responseData.status
                ? responseData.status
                : 'Integration connected successfully';

            // Update the integration locally to show it's connected
            // This prevents flickering that would occur with fetchIntegrations()
            if (apps) {
              const integration = apps.find(app => app.id === id);
              if (integration) {
                // Track integration connected event
                trackIntegrationConnected({
                  integrationId: id,
                  integrationType: integration.integration_type || '',
                  appName: integration.app_name || '',
                  isFirstIntegration: isFirstConnected,
                });

                // Update user property to indicate they have a connected integration
                addUserProperties({
                  integrationConnected: true,
                });

                updateIntegrationLocally(id, {
                  connected: true,
                  // Add any other fields that would be updated when connecting
                });

                // Show a success toast notification
                showToast({
                  priority: 'success',
                  content: statusMessage,
                  autoClose: 5000,
                });
              }
            }
            // Optionally fetch in the background to ensure data consistency
            // setTimeout(() => fetchIntegrations(), 2000);
          })
          .catch(error => {
            console.error('Error in connect_auth API call:', error);
            showToast({
              priority: 'error',
              content: `Error connecting integration: ${error.message || 'Unknown error'}`,
              autoClose: 5000,
            });
          });
      } catch (error) {
        console.error('Exception in connectAuth:', error);
        showToast({
          priority: 'error',
          content: `Error occurred while connecting authentication: ${error.message || 'Unknown error'}`,
          autoClose: 5000,
        });
      }
    },
    [apps, updateIntegrationLocally]
  );

  /**
   * Disconnect authentication for an integration
   * @param {String} id - Integration ID
   */
  const disconnectAuth = useCallback(
    async id => {
      const data = new FormData();
      data.append('integration_id', id);
      data.append('callback_url', `${window.location.origin}/callback`);
      localStorage.setItem('integration_id', id);

      try {
        await fetchWithAuth('/api/disconnect_auth/', {
          method: 'POST',
          body: data,
        })
          .then(response => {
            if (!response.ok) {
              throw new Error(
                `Server responded with status: ${response.status}`
              );
            }
            return response.json();
          })
          .then(responseData => {
            // Check if the response contains an error message
            if (responseData && responseData.error) {
              throw new Error(responseData.error || 'Unknown error occurred');
            }

            // Check if the response contains a status message
            const statusMessage =
              responseData && responseData.status
                ? responseData.status
                : 'Integration disconnected successfully';

            // Update the integration locally to show it's disconnected
            // This prevents flickering that would occur with fetchIntegrations()
            if (apps) {
              const integration = apps.find(app => app.id === id);
              if (integration) {
                // Check if there are other connected integrations (excluding the one being disconnected)
                const hasOtherConnectedIntegrations = apps.some(
                  app => app.id !== id && checkConnection(app)
                );

                // Track integration disconnected event
                trackIntegrationDisconnected({
                  integrationId: id,
                  integrationType: integration.integration_type || '',
                  appName: integration.app_name || '',
                  hasOtherConnectedIntegrations,
                });

                // Update user property if no integrations remain connected
                if (!hasOtherConnectedIntegrations) {
                  addUserProperties({
                    integrationConnected: false,
                  });
                }

                updateIntegrationLocally(id, {
                  connected: false,
                  // Add any other fields that would be updated when disconnecting
                });

                // Show a success toast notification
                showToast({
                  priority: 'success',
                  content: statusMessage,
                  autoClose: 5000,
                });
              }
            }

            // Optionally fetch in the background to ensure data consistency
            // setTimeout(() => fetchIntegrations(), 2000);
          })
          .catch(error => {
            console.error('Error in disconnect_auth API call:', error);
            showToast({
              priority: 'error',
              content: `Error disconnecting integration: ${error.message || 'Unknown error'}`,
              autoClose: 5000,
            });
          });
      } catch (error) {
        console.error('Exception in disconnectAuth:', error);
        showToast({
          priority: 'error',
          content: `Error occurred while disconnecting authentication: ${error.message || 'Unknown error'}`,
          autoClose: 5000,
        });
      }
    },
    [apps, updateIntegrationLocally]
  );

  /**
   * Send data to edit an integration
   * This function should only be called when we actually want to update the integration
   * It should not be called when just expanding an integration to view its details
   */
  const sendEditData = useCallback(async () => {
    setEditButtonLoading(true);

    // Check if we have a valid integration ID
    if (!int_id) {
      console.error('No integration ID provided for sendEditData');
      setEditButtonLoading(false);
      return;
    }

    // Validate that we have valid values for the fields we're updating
    // This prevents infinite loops when these values are empty or undefined
    if (
      periodCalTypeFlagInt === true &&
      (!periodCalTypeInt || periodCalTypeInt === '')
    ) {
      console.error(
        'Period Calendar Type is required when Period Calendar Flag is true'
      );
      setEditButtonLoading(false);
      return;
    }

    // Cash/Accrual is required unless we're only updating the app name
    const isNameOnlyUpdate =
      editedAppName &&
      editedAppName.trim() !== '' &&
      (cashAcInt === undefined || cashAcInt === null || cashAcInt === '');

    if (
      !isNameOnlyUpdate &&
      (cashAcInt === undefined || cashAcInt === null || cashAcInt === '')
    ) {
      console.error('Cash/Accrual value is required');
      setEditButtonLoading(false);
      return;
    }

    const data = new FormData();
    data.append('integrationId', int_id);

    // Only include app_name if it's a valid value
    if (editedAppName && editedAppName.trim() !== '') {
      data.append('app_name', editedAppName.trim());
    }

    // Only include periodCal if it's a valid value
    if (periodCalTypeInt && periodCalTypeInt !== '') {
      data.append('periodCal', periodCalTypeInt);
    }

    data.append('periodFlag', periodCalTypeFlagInt);

    // Only include cashAc if it's a valid value
    if (cashAcInt && cashAcInt !== '') {
      data.append('cashAc', cashAcInt);
    }

    data.append('enableBudgeting', enableBudgetingM);
    data.append('propotionalBudgeting', propotionalBudgetingM);
    data.append('yearBasedBudgeting', yearBasedBudgetingM);
    data.append('locationClassLevelBudgeting', locationClassLevelBudgetingM);

    try {
      await fetchWithAuth('/api/edit_integration/', {
        method: 'POST',
        body: data,
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`Server responded with status: ${response.status}`);
          }
          return response.json();
        })
        .then(responseData => {
          // Check if the response contains an error message
          if (responseData && responseData.error) {
            throw new Error(responseData.error || 'Unknown error occurred');
          }

          // Instead of refetching all integrations, update just the changed one
          // This prevents the flickering effect
          const localUpdate = {
            period_cal: periodCalTypeFlagInt,
            period_cal_type: periodCalTypeInt,
            cash_accrual: cashAcInt,
            enable_budgeting: enableBudgetingM,
            propotional_budgeting: propotionalBudgetingM,
            year_based_budgeting: yearBasedBudgetingM,
            location_class_level_budgeting: locationClassLevelBudgetingM,
          };
          // Only include app_name in local update if it was changed
          if (editedAppName && editedAppName.trim() !== '') {
            localUpdate.app_name = editedAppName.trim();
          }
          updateIntegrationLocally(int_id, localUpdate);

          // Clear edited app name after successful save to prevent stale values
          // being sent in subsequent API calls for different integrations
          setEditedAppName('');

          // Show a success toast notification
          showToast({
            priority: 'success',
            content: 'Integration updated successfully',
            autoClose: 5000,
          });

          // Optional: You can still fetch integrations in the background to ensure data consistency
          // but this won't cause flickering since we've already updated the UI
          // setTimeout(() => fetchIntegrations(), 2000);
        })
        .catch(error => {
          console.error('Error in edit_integration API call:', error);
          showToast({
            priority: 'error',
            content: `Error updating integration: ${error.message || 'Unknown error'}`,
            autoClose: 5000,
          });
        });
    } catch (error) {
      console.error('Exception in sendEditData:', error);
      showToast({
        priority: 'error',
        content: `Error occurred while editing integration: ${error.message || 'Unknown error'}`,
        autoClose: 5000,
      });
    }

    setEditButtonLoading(false);
    onClose();
  }, [
    int_id,
    editedAppName,
    periodCalTypeInt,
    periodCalTypeFlagInt,
    cashAcInt,
    enableBudgetingM,
    propotionalBudgetingM,
    yearBasedBudgetingM,
    locationClassLevelBudgetingM,
    updateIntegrationLocally, // Add the new function to dependencies
    setEditButtonLoading,
    onClose,
  ]);

  // Hotglue options
  const options = useMemo(
    () => ({
      hideBackButtons: true,
      breadcrumbs: false,
      tenantMetadata: metadata,
      listener: {
        onSourceLinked: (_source, flow) => {
          connectAuth(int_id);
          createJob(flow, tenant);
          setSyncButtonLoading(false);
        },
        onSourceUnlinked: () => {
          disconnectAuth(int_id);
          setSyncButtonLoading(false);
        },
        onPopupOpen: () => {
          setConnectModal(false);
          setSaveBtnLoading(false);
        },
        onPopupClose: () => {
          setSyncButtonLoading(false);
        },
        onWidgetClose: () => {
          setSyncButtonLoading(false);
        },
        onWidgetOpen: () => {
          setConnectModal(false);
          setSaveBtnLoading(false);
        },
      },
    }),
    [
      metadata,
      int_id,
      tenant,
      connectAuth,
      disconnectAuth,
      createJob,
      setConnectModal,
      setSaveBtnLoading,
      setSyncButtonLoading,
    ]
  );

  // Handle edit data submission when flag changes
  // We only want to trigger this effect when sendEditDataFlag changes
  // and not when int_id or periodCalTypeFlagInt change
  const lastSendEditDataFlagRef = useRef(sendEditDataFlag);
  const isProcessingRef = useRef(false);

  useEffect(() => {
    // Only call sendEditData when sendEditDataFlag actually changes
    // This prevents update loops when expanding integrations
    if (
      sendEditDataFlag !== lastSendEditDataFlagRef.current &&
      !isProcessingRef.current
    ) {
      lastSendEditDataFlagRef.current = sendEditDataFlag;

      // Set processing flag to prevent multiple calls
      isProcessingRef.current = true;

      // Validate that we have the necessary data before making the API call
      // Allow name-only updates when editedAppName has a value
      const hasNameToUpdate = editedAppName && editedAppName.trim() !== '';
      const hasOtherFieldsToUpdate =
        cashAcInt &&
        cashAcInt !== '' &&
        (!periodCalTypeFlagInt ||
          (periodCalTypeFlagInt &&
            periodCalTypeInt &&
            periodCalTypeInt !== ''));
      const hasValidData =
        int_id && (hasNameToUpdate || hasOtherFieldsToUpdate);

      if (sendEditDataFlag && hasValidData) {
        // Capture the current int_id for callback lookup
        const currentIntId = int_id;
        sendEditData()
          .then(() => {
            // Call the completion callback for this specific integration
            const callback =
              onEditCompleteCallbacksRef.current.get(currentIntId);
            if (callback) {
              callback({ success: true });
              onEditCompleteCallbacksRef.current.delete(currentIntId);
            }
          })
          .catch(error => {
            // Call the completion callback for this specific integration
            const callback =
              onEditCompleteCallbacksRef.current.get(currentIntId);
            if (callback) {
              callback({ success: false, error });
              onEditCompleteCallbacksRef.current.delete(currentIntId);
            }
          })
          .finally(() => {
            // Reset processing flag when done
            isProcessingRef.current = false;
          });
      } else if (sendEditDataFlag) {
        console.error('Not calling sendEditData() due to invalid data');
        setEditButtonLoading(false);
        isProcessingRef.current = false;
      } else {
        isProcessingRef.current = false;
      }
    }
  }, [
    sendEditDataFlag,
    int_id,
    editedAppName,
    periodCalTypeFlagInt,
    cashAcInt,
    periodCalTypeInt,
    sendEditData,
    setEditButtonLoading,
  ]);

  /**
   * Handle authentication for an integration
   * @param {String|Object} idOrItem - Integration ID or full integration item object
   *
   * Note: This function is only used for Quickbooks/Intuit authentication.
   * For Xero and other integrations, we use onResyncClick in ReskinnedIntegrationList.jsx
   * which calls handleResyncClick() to handle the connection flow.
   */
  const handleAuth = async idOrItem => {
    setSyncButtonLoading(true);

    // Support both old (id only) and new (full item object) calling patterns
    const id = typeof idOrItem === 'object' ? idOrItem.id : idOrItem;
    const item =
      typeof idOrItem === 'object'
        ? idOrItem
        : apps.find(app => app.id === idOrItem);

    if (tpc) {
      options.tenantMetadata = metadata;
      link(
        companyId && '_' && newIntegration,
        hotglueFlowId,
        sourceId,
        false,
        options
      );
    } else {
      // This is for Quickbooks/Intuit authentication only
      // Xero authentication is handled by handleResyncClick
      const data = new FormData();
      data.append('client_id', '');
      data.append('secret_key', '');
      data.append('inuit_company_id', '');
      data.append('type', '');
      data.append('integration_id', id);
      data.append('callback_url', `${window.location.origin}/callback`);
      localStorage.setItem('integration_id', id);

      // Store integration details for analytics tracking in callback handler
      if (item) {
        localStorage.setItem('integration_type', item.integration_type || '');
        localStorage.setItem('integration_app_name', item.app_name || '');
        localStorage.setItem('integration_desc', item.integration_desc || '');
      }

      try {
        await fetchWithAuth('/api/inuit_auth/', {
          method: 'POST',
          body: data,
        })
          .then(response => {
            if (!response.ok) {
              throw new Error(
                `Server responded with status: ${response.status}`
              );
            }
            return response.json();
          })
          .then(responseData => {
            // Check if the response contains an error message
            if (responseData && responseData.error) {
              throw new Error(responseData.error || 'Unknown error occurred');
            }

            redirect(responseData);

            // Show a success toast notification
            showToast({
              priority: 'success',
              content: 'Authentication initiated successfully',
              autoClose: 5000,
            });
          })
          .catch(error => {
            console.error('Error in inuit_auth API call:', error);
            showToast({
              priority: 'error',
              content: `Error during authentication: ${error.message || 'Unknown error'}`,
              autoClose: 5000,
            });
          });
      } catch (error) {
        console.error('Exception in handleAuth:', error);
        showToast({
          priority: 'error',
          content: `Error occurred during authentication: ${error.message || 'Unknown error'}`,
          autoClose: 5000,
        });
      }
      setSyncButtonLoading(false);
    }
  };

  /**
   * Redirect to a URL
   * @param {String} url - URL to redirect to
   */
  const redirect = async url => {
    setModalButtonLoading(false);
    setModalOpen(false);
    try {
      const { redirectToIntuitOAuth } = await import(
        '../../../utils/intuitSessionUtils'
      );
      redirectToIntuitOAuth(url);
    } catch (_e) {
      // Fallback: open directly if dynamic import fails
      window.location.href = url;
    }
  };

  /**
   * Build FormData for integration
   * @param {Object} params - Integration parameters
   * @param {string} params.appName - Application name
   * @param {string} params.integrationType - Type of integration
   * @param {boolean} params.captureLocation - Whether to capture location
   * @param {boolean} params.locationAttr - Whether to use location attribute
   * @param {string} params.country - Selected country
   * @param {boolean} params.periodCalFlag - Whether period calendar is enabled
   * @param {string} params.periodCalType - Type of period calendar
   * @param {string} params.cashAc - Cash or accrual accounting
   * @returns {FormData} FormData object with integration fields
   */
  const buildIntegrationFormData = ({
    appName,
    integrationType,
    captureLocation,
    locationAttr,
    country,
    periodCalFlag,
    periodCalType,
    cashAc,
  }) => {
    const data = new FormData();
    data.append('app_name', appName);
    data.append('integration_type', integrationType);
    data.append('capture_location', captureLocation ? 'true' : 'false');
    data.append('location_attr', locationAttr ? 'true' : 'false');
    data.append('country', country);
    data.append('period_cal', periodCalFlag);
    data.append('period_cal_type', periodCalType);
    data.append('cashAc', cashAc);
    return data;
  };

  /**
   * Add a new integration (handles both regular and TPC integrations)
   * @param {boolean} isTpc - Whether this is a TPC integration
   */
  const addIntegration = async (isTpc = false) => {
    // Reset TPC-specific state if needed
    if (isTpc) {
      setCompanyId(undefined);
      setInt_id(undefined);
    }

    setSaveBtnLoading(true);

    // Validate app name
    if (!appName || appName.trim() === '') {
      setSaveBtnLoading(false);
      showToast({
        priority: 'error',
        content: 'Please enter the integration name',
        autoClose: 5000,
      });
      return;
    }

    try {
      const response = await fetchWithAuth('/api/add_integration/', {
        method: 'POST',
        body: buildIntegrationFormData({
          appName,
          integrationType: integration_type,
          captureLocation,
          locationAttr,
          country: countrySelected,
          periodCalFlag: periodCalTypeFlag,
          periodCalType,
          cashAc,
        }),
      });

      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      const responseData = await response.json();

      // Check for error in response
      if (responseData?.error) {
        throw new Error(responseData.error || 'Unknown error occurred');
      }

      // Track integration added event
      const isFirstAdded = isFirstIntegrationAdded();
      trackIntegrationAdded({
        integrationId: responseData.last_integration_id || '',
        integrationType: integration_type,
        appName: appName,
        isFirstIntegration: isFirstAdded,
      });

      // Handle TPC-specific state updates
      if (isTpc) {
        setCompanyId(responseData.company_id);
        setInt_id(responseData.last_integration_id);
        setHotglueNewLink(true);
      }

      fetchIntegrations();

      // Show success toast
      showToast({
        priority: 'success',
        content: 'Integration added successfully',
        autoClose: 5000,
      });

      // Close modal for non-TPC integrations
      if (!isTpc) {
        setConnectModal(!connectModal);
      }
    } catch (error) {
      console.error('Error adding integration:', error);
      showToast({
        priority: 'error',
        content: `Error adding integration: ${error.message || 'Unknown error'}`,
        autoClose: 5000,
      });
    } finally {
      setSaveBtnLoading(false);
    }
  };

  // Handle hotglue link when needed
  useEffect(() => {
    if (hotglueNewLink && companyId !== undefined && int_id !== undefined) {
      options.tenantMetadata = metadata;
      link(companyId + '_' + int_id, hotglueFlowId, sourceId, false, options);
      setHotglueNewLink(false);
    }
  }, [
    hotglueNewLink,
    companyId,
    int_id,
    hotglueFlowId,
    sourceId,
    metadata,
    link,
    options,
    setHotglueNewLink,
  ]);

  /**
   * Format date for form data or handle accounting format changes
   * @param {Array|String} value - Date range array or accounting format string
   * @param {String} intId - Integration ID
   */
  const setFormDataDate = (value, intId) => {
    // Check if value is a string (accounting format) or an array (date range)
    if (typeof value === 'string') {
      // Handle accounting format change
      // Find the integration and update its accounting_format
      if (apps) {
        const updatedApps = apps.map(app => {
          if (app.id === intId) {
            return { ...app, accounting_format: value };
          }
          return app;
        });
        setApps(updatedApps);
      }
      return;
    }

    // Handle date range
    if (
      Array.isArray(value) &&
      value.length >= 2 &&
      value[0] instanceof Date &&
      value[1] instanceof Date
    ) {
      const fromDate =
        (value[0].getMonth() > 8
          ? value[0].getMonth() + 1
          : '0' + (value[0].getMonth() + 1)) +
        '-' +
        (value[0].getDate() > 9
          ? value[0].getDate()
          : '0' + value[0].getDate()) +
        '-' +
        value[0].getFullYear();

      const toDate =
        (value[1].getMonth() > 8
          ? value[1].getMonth() + 1
          : '0' + (value[1].getMonth() + 1)) +
        '-' +
        (value[1].getDate() > 9
          ? value[1].getDate()
          : '0' + value[1].getDate()) +
        '-' +
        value[1].getFullYear();

      setExcelUploadData({
        from_date: fromDate,
        to_date: toDate,
        integration_id: intId,
        value: value,
      });
    }
  };

  // Fetch integrations and country data on component mount
  useEffect(() => {
    fetchIntegrations();
    fetchCountry();
    const state = store.getState();
    setCompanySwitcherActive(state.dateFormat.companySwitcherActive);

    // Initialize individual state values instead of using formState
    setIntegration_type('');
    setCountrySelected('');
    setPeriodCalTypeFlag(false);
    setPeriodCalType(defaultPeriod);
    setCashAc('');
    setCaptureLocation(false);
    setLocationAttr(false);
    setCaptureLocationDisplay(false);
    setLocationAttrDisplay(false);
    setAttributeType(false);
  }, [fetchIntegrations, fetchCountry, defaultPeriod]);

  /**
   * Returns currency string from country ID
   * @param {String} countryInput - Country ID
   * @returns {String} - Currency string
   */
  const returnCurrencyFromCountry = countryInput => {
    let currency = '';
    if (country !== undefined) {
      country.forEach(obj => {
        if (obj.value === countryInput) {
          currency = obj.country_label + ' (' + obj.currency + ')';
        }
      });
    }
    return currency;
  };

  /**
   * Handle integration item click
   * @param {Object} item - Integration item
   *
   * This function is called when an integration is clicked/expanded
   * It should only update the state with the integration's data
   * It should NOT trigger any API calls
   */
  const handleIntegrationItemClick = useCallback(
    item => {
      // We're just updating state with the integration's data
      // No need to compare with previous values since we're not making API calls

      // Update state with the integration's data
      setInt_id(item.id);
      setTpc(item.tpc || false);
      setHotglueFlowId(item.flow_id || '');
      setSourceId(item.source_id || '');

      // Ensure we have valid values for these critical fields
      // This prevents issues with empty or undefined values
      setPeriodCalTypeFlagInt(item.period_cal === true);

      // Set periodCalTypeInt only if it's a valid value
      if (item.period_cal_type && item.period_cal_type !== '') {
        setPeriodCalTypeInt(item.period_cal_type);
      } else if (item.period_cal === true) {
        // If period_cal is true but period_cal_type is empty, use a default value
        // This prevents issues with the backend expecting a value when periodFlag is true
        const defaultPeriodType =
          periodTypes && periodTypes.length > 0 ? periodTypes[0].value[0] : '';
        setPeriodCalTypeInt(defaultPeriodType);
      }

      // Set cashAcInt only if it's a valid value
      if (item.cash_accrual && item.cash_accrual !== '') {
        setCashAcInt(item.cash_accrual);
      } else {
        // Use a default value if cash_accrual is empty
        setCashAcInt('Cash'); // Default to Cash if not specified
      }

      setEnableBudgeting(item.enable_budgeting || false);
      setYearBasedBudgeting(item.year_based_budgeting || false);
      setPropotionalBudgeting(item.propotional_budgeting || false);
      setLocationClassLevelBudgeting(
        item.location_class_level_budgeting || false
      );
      setEnableBudgetingM(item.enable_budgeting || false);
      setLocationAttrM(item.location_attr || false);
    },
    [int_id, periodCalTypeFlagInt, periodTypes]
  );

  /**
   * Handle budget settings button click
   * @param {String} integrationId - Integration ID (optional)
   */
  const handleBudgetSettingsClick = integrationId => {
    // If an integrationId is provided, find that integration and load its settings
    if (integrationId && apps) {
      const selectedIntegration = apps.find(app => app.id === integrationId);
      if (selectedIntegration) {
        // Update the current integration ID
        setInt_id(selectedIntegration.id);
        // Load this integration's budget settings
        setEnableBudgetingM(selectedIntegration.enable_budgeting);
        setYearBasedBudgetingM(selectedIntegration.year_based_budgeting);
        setPropotionalBudgetingM(selectedIntegration.propotional_budgeting);
        setLocationClassLevelBudgetingM(
          selectedIntegration.location_class_level_budgeting
        );
        setLocationAttrM(selectedIntegration.location_attr);
      }
    } else {
      // If no integrationId is provided, use the current settings
      setEnableBudgetingM(enableBudgeting);
      setYearBasedBudgetingM(yearBasedBudgeting);
      setPropotionalBudgetingM(propotionalBudgeting);
      setLocationClassLevelBudgetingM(locationClassLevelBudgeting);
    }

    // Open the modal
    setEnableBudgetingModal(!enableBudgetingModal);
  };

  /**
   * Handle uploader change
   * @param {Array} filelist - File list
   * @param {String} itemId - Integration ID
   */
  const handleUploaderChange = (filelist, itemId) => {
    if (filelist.length >= 1) {
      if (limitUploader.length === 0) {
        setLimitUploader(objToJson(itemId, true));
      } else {
        const newLimitUploader = { ...limitUploader };
        newLimitUploader[itemId] = true;
        setLimitUploader(newLimitUploader);
      }
    } else {
      if (limitUploader.length === 0) {
        setLimitUploader(objToJson(itemId, false));
      } else {
        const newLimitUploader = { ...limitUploader };
        newLimitUploader[itemId] = false;
        setLimitUploader(newLimitUploader);
      }
    }
  };

  /**
   * Handle resync button click
   * @param {Object} item - Integration item
   *
   * Note: This function is used for both resyncing existing connections
   * and for connecting to non-Quickbooks integrations like Xero.
   * The ReskinnedIntegrationList component uses this function for the
   * Connect button when the integration is not Quickbooks.
   */
  const handleResyncClick = item => {
    setTenant(`${item.company_id_id}_${item.id}`);
    setSyncButtonLoading(true);
    options.tenantMetadata = metadata;
    link(
      item.company_id_id + '_' + item.id,
      item.flow_id,
      item.source_id,
      false,
      options
    );
  };

  /**
   * Handle add integration button click
   */
  const handleAddIntegration = () => {
    // Reset individual state values
    setAppName('');
    setIntegration_type('');
    setCountrySelected('');
    setPeriodCalTypeFlag(false);
    setPeriodCalType(defaultPeriod);
    setCashAc('');
    setCaptureLocation(false);
    setLocationAttr(false);
    setCaptureLocationDisplay(false);
    setLocationAttrDisplay(false);
    setAttributeType(false);
    setTpc(false);

    setConnectModal(!connectModal);
    setSaveBtnLoading(false);
  };

  /**
   * Handle add integration save
   * @param {Object} response - Response from the API (optional, used by ReskinnedAddIntegrationModal)
   */
  const handleAddIntegrationSave = response => {
    // If response is provided, it means the save was already handled by ReskinnedAddIntegrationModal
    if (response) {
      // Update the local state with the new integration data
      fetchIntegrations();

      // If it's a TPC integration, set the necessary state
      if (tpc) {
        setCompanyId(response.company_id);
        setInt_id(response.last_integration_id);
        setHotglueNewLink(true);
      }

      // Close the modal
      setConnectModal(false);
    } else {
      // Legacy implementation for non-reskinned version
      addIntegration(tpc);
    }
  };

  /**
   * Handle budget settings save
   */
  const handleBudgetSettingsSave = () => {
    // If showReskin is true, save directly without confirmation dialog
    if (showReskin) {
      setSendEditDataFlag(!sendEditDataFlag);
      setEnableBudgetingModal(false);
    } else {
      // Otherwise, show the confirmation dialog
      onOpen();
    }
  };

  /**
   * Handle budget settings cancel
   */
  const handleBudgetSettingsCancel = () => {
    setEnableBudgetingModal(!enableBudgetingModal);
  };

  /**
   * Handle budget alert confirm
   */
  const handleBudgetAlertConfirm = () => {
    setSendEditDataFlag(!sendEditDataFlag);
    setEnableBudgetingModal(!enableBudgetingModal);
  };

  // Create budget settings object for the modal
  const budgetSettings = {
    enableBudgeting: enableBudgetingM,
    locationClassLevelBudgeting: locationClassLevelBudgetingM,
    yearBasedBudgeting: yearBasedBudgetingM,
    propotionalBudgeting: propotionalBudgetingM,
    locationAttr: locationAttrM,
  };

  // Create form state object for the add integration modal
  const formState = {
    appName,
    captureLocation,
    locationAttr,
    integration_type,
    captureLocationDisplay,
    locationAttrDisplay,
    attributeType,
    countrySelected,
    periodCalType,
    periodCalTypeFlag,
    cashAc,
    tpc,
    hotglueFlowId,
    sourceId,
  };

  // Set budget settings state
  const setBudgetSettings = settings => {
    setEnableBudgetingM(settings.enableBudgeting);
    setLocationClassLevelBudgetingM(settings.locationClassLevelBudgeting);
    setYearBasedBudgetingM(settings.yearBasedBudgeting);
    setPropotionalBudgetingM(settings.propotionalBudgeting);
  };

  // Set form state
  const setFormState = state => {
    if (state.appName !== undefined) setAppName(state.appName);
    setCaptureLocation(state.captureLocation);
    setLocationAttr(state.locationAttr);
    setIntegration_type(state.integration_type);
    setCaptureLocationDisplay(state.captureLocationDisplay);
    setLocationAttrDisplay(state.locationAttrDisplay);
    setAttributeType(state.attributeType);
    setCountrySelected(state.countrySelected);
    setPeriodCalType(state.periodCalType);
    setPeriodCalTypeFlag(state.periodCalTypeFlag);
    setCashAc(state.cashAc);
    setTpc(state.tpc);
    setHotglueFlowId(state.hotglueFlowId);
    setSourceId(state.sourceId);
  };

  return (
    <Card p={1} minH={700}>
      {/* Conditionally render the CardHeader based on the showReskin feature flag */}
      {showReskin ? (
        <CardHeader p={4} pt={0}>
          <Flex
            width={'100%'}
            justifyContent={'space-between'}
            alignItems={'center'}
          >
            <Text
              fontFamily="sans-serif"
              fontWeight={600}
              fontSize="32px"
              lineHeight="1.25em"
              color="#0A091B"
            >
              Integrations
            </Text>
            {/* Only show Add Integration button when apps exist */}
            {apps !== undefined && apps.length > 0 && (
              <NexusButton
                variant="filled"
                leadingIcon={AddIcon}
                onClick={handleAddIntegration}
              >
                Add Integration
              </NexusButton>
            )}
          </Flex>
        </CardHeader>
      ) : (
        <CardHeader p={2}>
          <Flex width={'100%'} justifyContent={'flex-end'}>
            <RsButton
              size={'sm'}
              appearance="default"
              onClick={handleAddIntegration}
            >
              <Icon as={FaPlus} mr={2} />
              <Text>Add Integration</Text>
            </RsButton>
          </Flex>
        </CardHeader>
      )}
      <CardBody>
        <IntegrationList
          apps={apps}
          onAddIntegration={handleAddIntegration}
          onItemClick={handleIntegrationItemClick}
          onPeriodCalTypeChange={value => {
            setPeriodCalTypeInt(value);
            setSendEditDataFlag(!sendEditDataFlag);
          }}
          onCashAcChange={value => {
            setCashAcInt(value);
            // Don't trigger API call here - it will be triggered by the Edit button
          }}
          onAppNameChange={value => {
            setEditedAppName(value);
            // Don't trigger API call here - it will be triggered when user finishes editing
          }}
          onEditClick={() => setSendEditDataFlag(!sendEditDataFlag)}
          onSetIntegrationId={setInt_id}
          setOnEditComplete={(integrationId, callback) => {
            // Register callback by integration ID to prevent race conditions
            onEditCompleteCallbacksRef.current.set(integrationId, callback);
          }}
          onBudgetSettingsClick={handleBudgetSettingsClick}
          onAuthClick={handleAuth}
          onDisconnectClick={id => {
            disconnectAuth(id);
            // fetchIntegrations() is already called inside disconnectAuth
          }}
          onDateRangeChange={setFormDataDate}
          onUploaderChange={handleUploaderChange}
          onResyncClick={handleResyncClick}
          editButtonLoading={editButtonLoading}
          syncButtonLoading={syncButtonLoading}
          periodCalTypeFlagInt={periodCalTypeFlagInt}
          setPeriodCalTypeFlagInt={setPeriodCalTypeFlagInt}
          sendEditDataFlag={sendEditDataFlag}
          setSendEditDataFlag={setSendEditDataFlag}
          enableBudgeting={enableBudgeting}
          yearBasedBudgeting={yearBasedBudgeting}
          propotionalBudgeting={propotionalBudgeting}
          locationClassLevelBudgeting={locationClassLevelBudgeting}
          limitUploader={limitUploader}
          excelUploadData={excelUploadData}
          country={country}
          periodTypes={periodTypes}
          cashAccData={cashAccData}
          companySwitcherActive={companySwitcherActive}
          apiEndpoint={apiEndpoint}
          returnCurrencyFromCountry={returnCurrencyFromCountry}
        />

        {/* Budget Settings Modal */}
        <BudgetSettingsModal
          isOpen={enableBudgetingModal}
          onClose={() => setEnableBudgetingModal(false)}
          onSave={handleBudgetSettingsSave}
          onCancel={handleBudgetSettingsCancel}
          settings={budgetSettings}
          setSettings={setBudgetSettings}
          saveBtnLoading={saveBtnLoading}
        />

        {/* Budget Alert Dialog - only show when showReskin is false */}
        {!showReskin && (
          <BudgetAlertDialog
            isOpen={isOpen}
            onClose={onClose}
            onConfirm={handleBudgetAlertConfirm}
            cancelRef={cancelRef}
            editButtonLoading={editButtonLoading}
          />
        )}

        {/* Add Integration Modal */}
        <AddIntegrationModal
          isOpen={connectModal}
          onClose={() => setConnectModal(false)}
          onSave={handleAddIntegrationSave}
          saveBtnLoading={saveBtnLoading}
          formState={formState}
          setFormState={setFormState}
          country={country}
          integrationTypes={integrationTypes}
          periodTypes={periodTypes}
          defaultPeriod={defaultPeriod}
          cashAccData={cashAccData}
          allIntegrationTypes={allIntegrationTypes}
          companySwitcherActive={companySwitcherActive}
          apps={apps}
          isFirstIntegration={isFirstIntegrationAdded}
          companyName={metadata?.company_name}
        />
      </CardBody>
    </Card>
  );
}
